// src/app/pages/auth/login/login.component.ts
import { Component, inject, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { AppConfigurator } from '../../../layout/component/app.configurator';

import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { filter, Subject, takeUntil } from 'rxjs';
import { authoritySUSI } from '../msal.config';
import { HealthCheckService } from '../../../core/shared/health-check.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ButtonModule, CardModule, AppConfigurator],
  templateUrl: './login.component.html'
})
export class Login implements OnDestroy {
  private router = inject(Router);
  private msal = inject(MsalService);
  private msalBroadcast = inject(MsalBroadcastService);
  private healthCheck = inject(HealthCheckService);

  signingIn = false;
  private destroy$ = new Subject<void>();

  constructor() {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(() => (this.signingIn = false));

    // Fires once per login-completion transition (interaction goes from
    // in-progress -> None). Guards against firing on unrelated
    // in-progress -> None transitions where there is no account yet
    // (e.g. app boot before any login attempt).
    this.msalBroadcast.inProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status: InteractionStatus) => {
        const wasSigningIn = this.signingIn;
        this.signingIn = status !== InteractionStatus.None;

        if (wasSigningIn && status === InteractionStatus.None) {
          const account =
            this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0] ?? null;
          if (account) {
            this.msal.instance.setActiveAccount(account);
            this.healthCheck.triggerHealthCheck();
          }
        }
      });
  }

  ngOnInit() {
    // If already authenticated (account exists), skip login page.
    // Also counts as "post-login" for health-check purposes since no
    // interactive redirect will occur in this session.
    const account =
      this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0] ?? null;

    if (account) {
      this.msal.instance.setActiveAccount(account);
      this.healthCheck.triggerHealthCheck();
      this.router.navigate(['/home']);
    }
  }

  goToDashboard() {
    if (this.signingIn) return;

    const request: RedirectRequest = {
      authority: authoritySUSI,
      scopes: ['openid', 'profile', 'offline_access'],
      prompt: 'select_account'
    };

    this.msal.loginRedirect(request);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}